import axios, { AxiosError, AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { clearAuth, getRefreshToken, getToken, setRefreshToken, setToken } from "../auth/auth";
import { disconnectRealtime, reauthRealtime } from "../realtime/socket";
import { getRuntimeEnv } from "../config/runtimeEnv";

const envApiBase = getRuntimeEnv("REACT_APP_API_URL");
const defaultDevApiBase = "http://localhost:3002";
const isDevelopment = process.env.NODE_ENV === "development";

export const API_BASE = envApiBase || (isDevelopment ? defaultDevApiBase : "");

if (!API_BASE) {
  throw new Error("Missing REACT_APP_API_URL. Set Cloud Run runtime envs (env.js) or build-time env.");
}
export const API_V1 = `${API_BASE}/api/v1`;

export interface AppApiError extends Error {
  status: number;
  details?: unknown;
  code?: string;
}

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const AUTH_PUBLIC_PATHS = new Set(["/auth/login", "/auth/register", "/auth/refresh"]);

function isPublicPath(url?: string): boolean {
  if (!url) return false;
  return AUTH_PUBLIC_PATHS.has(url);
}

function toAppApiError(error: unknown): AppApiError {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as
      | { message?: string | string[]; error?: string; code?: string; details?: unknown }
      | undefined;

    const fallbackMessage = error.message || "Request failed";
    const messageFromPayload = Array.isArray(responseData?.message)
      ? responseData?.message.join(", ")
      : responseData?.message || responseData?.error;

    const appError = new Error(messageFromPayload || fallbackMessage) as AppApiError;
    appError.status = error.response?.status ?? 0;
    appError.details = responseData?.details ?? responseData;
    if (responseData?.code !== undefined) {
      appError.code = responseData.code;
    }
    return appError;
  }

  const appError = new Error("Request failed") as AppApiError;
  appError.status = 0;
  return appError;
}

function attachAuthToken(config: InternalAxiosRequestConfig, token: string | null): InternalAxiosRequestConfig {
  if (!token || isPublicPath(config.url)) return config;

  const headers = config.headers instanceof AxiosHeaders ? config.headers : new AxiosHeaders(config.headers);
  headers.set("Authorization", `Bearer ${token}`);
  config.headers = headers;
  return config;
}

function createClient(): AxiosInstance {
  return axios.create({
    baseURL: API_V1,
    timeout: 30000,
  });
}

export const publicClient = createClient();
export const apiClient = createClient();

apiClient.interceptors.request.use((config) => attachAuthToken(config, getToken()));

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = (error.config || {}) as RetryConfig;
    const status = error.response?.status;

    if (status === 401 && !originalConfig._retry && !isPublicPath(originalConfig.url) && originalConfig.url !== "/auth/refresh") {
      originalConfig._retry = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        disconnectRealtime();
        clearAuth();
        throw toAppApiError(error);
      }

      try {
        const refreshRes = await publicClient.post("/auth/refresh", { refreshToken });
        const refreshed = refreshRes.data as { token?: string; refreshToken?: string };
        if (!refreshed?.token) {
          disconnectRealtime();
          clearAuth();
          throw toAppApiError(error);
        }

        setToken(refreshed.token);
        if (refreshed.refreshToken) {
          setRefreshToken(refreshed.refreshToken);
        }

        // Re-handshake the realtime socket with the rotated token so chat /
        // notifications don't silently stall on the stale credential.
        reauthRealtime();

        const retryHeaders =
          originalConfig.headers instanceof AxiosHeaders
            ? originalConfig.headers
            : new AxiosHeaders(originalConfig.headers);
        retryHeaders.set("Authorization", `Bearer ${refreshed.token}`);
        originalConfig.headers = retryHeaders;

        return apiClient.request(originalConfig);
      } catch (refreshError) {
        disconnectRealtime();
        clearAuth();
        throw toAppApiError(refreshError);
      }
    }

    throw toAppApiError(error);
  }
);

export function getErrorMessage(error: unknown, fallback = "Request failed"): string {
  if (error && typeof error === "object" && "message" in error && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message || fallback;
  }
  return fallback;
}
